// Subscription prices: every creator sets their own, within one range. The one place the numbers live;
// the Worker (API), the studio (editor) and the tests all import this file, and the database enforces
// the same range (supabase/migrations/010_creator_pricing.sql), so they can't drift apart silently.
// Keep it free of imports: Node runs it directly in tests.

export const MIN_PRICE_CENTS = 680; // $6.80
export const MAX_PRICE_CENTS = 20_000; // $200.00
export const PLATFORM_FEE_BPS = 1000; // AfterHourz keeps 10% of each subscription payment (basis points: 100 = 1%); processing fees come out too
export const PLATFORM_FEE_PERCENT = PLATFORM_FEE_BPS / 100;
// Earnings are held before a creator can withdraw them, to cover refunds and chargebacks. Staff can set 7 to 14 days.
export const PAYOUT_HOLD_MIN_DAYS = 7;
export const PAYOUT_HOLD_MAX_DAYS = 14;
export const PAYOUT_HOLD_DEFAULT_DAYS = 14;
export const DEFAULT_PRICE_CENTS = { supporter: 680, vip: 1499 } as const;
export type PricedTier = keyof typeof DEFAULT_PRICE_CENTS;
export const TIERS: readonly PricedTier[] = ['supporter', 'vip'];

export const formatCents = (cents: number): string => `$${(cents / 100).toFixed(2)}`;
export const PRICE_RANGE_TEXT = `${formatCents(MIN_PRICE_CENTS)} to ${formatCents(MAX_PRICE_CENTS)}`;

/** "6.80", "$200", "15.5" → cents. Strict: digits with at most two decimals, no commas or signs; anything else is null. */
export function parseDollars(input: string): number | null {
  const m = String(input).trim().replace(/^\$/, '').match(/^(\d{1,3})(?:\.(\d{1,2}))?$/);
  if (!m) return null;
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0') || 0);
}

/** Like parseDollars, but for balances and withdrawals, which can be far above $999 (up to 7 digits: $9,999,999.99). */
export function parseMoney(input: string): number | null {
  const m = String(input).trim().replace(/^\$/, '').match(/^(\d{1,7})(?:\.(\d{1,2}))?$/);
  if (!m) return null;
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0') || 0);
}

/** An error message for one price, or null if it is fine. */
export function priceProblem(cents: unknown): string | null {
  if (typeof cents !== 'number' || !Number.isInteger(cents)) return 'Enter a price like 6.80.';
  if (cents < MIN_PRICE_CENTS || cents > MAX_PRICE_CENTS) return `Prices must be between ${PRICE_RANGE_TEXT}.`;
  return null;
}

/** Checks a Supporter + VIP pair. Returns the cleaned prices, or an error message string. */
export function validatePrices(supporter: unknown, vip: unknown): { supporter: number; vip: number } | string {
  const problem = priceProblem(supporter) ?? priceProblem(vip);
  if (problem) return problem;
  if ((vip as number) < (supporter as number)) return 'VIP must cost at least as much as Supporter, because it includes everything in Supporter.';
  return { supporter: supporter as number, vip: vip as number };
}

export const BENEFITS_MAX_ITEMS = 8;
export const BENEFIT_MAX_LENGTH = 100;

/** A tier's benefits from a list of lines (blank lines dropped, whitespace trimmed). Returns the cleaned list, or an error message string. */
export function cleanBenefits(input: unknown): string[] | string {
  if (!Array.isArray(input) || input.some((b) => typeof b !== 'string')) return 'Benefits must be a list of text lines.';
  const lines = (input as string[]).map((b) => b.replace(/\s+/g, ' ').trim()).filter(Boolean);
  if (lines.length > BENEFITS_MAX_ITEMS) return `List at most ${BENEFITS_MAX_ITEMS} benefits per tier.`;
  if (lines.some((b) => b.length > BENEFIT_MAX_LENGTH)) return `Keep each benefit under ${BENEFIT_MAX_LENGTH} characters.`;
  return lines;
}

/** The platform's cut of one payment, rounded to the nearest cent (halves round up). The database rounds the same way. */
export const platformFeeCents = (grossCents: number): number => Math.round((grossCents * PLATFORM_FEE_BPS) / 10_000);

/** What the creator receives from one payment before payment-processing fees. */
export const creatorShareCents = (grossCents: number): number => Math.max(0, grossCents - platformFeeCents(grossCents));

// Tips: a one-off amount a viewer sends a creator from a video. Same rule everywhere (Worker, watch page, tests).
export const MIN_TIP_CENTS = 300; // $3.00: smaller tips would be eaten by payment-processing fees
export const MAX_TIP_CENTS = 50_000; // $500.00
export const TIP_PRESETS_CENTS = [500, 1000, 2000, 5000] as const;
export const TIP_MESSAGE_MAX = 200;
export const TIP_RANGE_TEXT = `${formatCents(MIN_TIP_CENTS)} to ${formatCents(MAX_TIP_CENTS)}`;

/** An error message for one tip amount, or null if it is fine. */
export function tipProblem(cents: unknown): string | null {
  if (typeof cents !== 'number' || !Number.isInteger(cents)) return 'Enter an amount like 5.00.';
  if (cents < MIN_TIP_CENTS || cents > MAX_TIP_CENTS) return `Tips must be between ${TIP_RANGE_TEXT}.`;
  return null;
}
